import React from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import { ChevronSvg } from '@components/adgroup-svg/react'

import PropertyOption, { Option } from './property-option'

interface SelectPropertyProps {
  label: string
  options: Option[]
  value: string
  onChange: (value: string) => void
  canDeselect?: boolean
  placeholder?: string
  expandOptions: boolean
  onClick: () => void
  showAsThumbnail?: boolean
}

function SelectProperty({
  label,
  options,
  value,
  onChange,
  canDeselect,
  placeholder = 'Select',
  expandOptions,
  onClick,
  showAsThumbnail,
}: SelectPropertyProps) {
  const selectedOption = React.useMemo(
    () => options.find((option) => option.id === value),
    [value, options]
  )
  const showOptions = React.useMemo(
    () => !!options.length && expandOptions,
    [expandOptions, options]
  )
  const [transitionState, setTransitionState] = React.useState<
    'enter' | 'exited'
  >('exited')

  const handleClick = React.useCallback(() => {
    if (!options.length) {
      return
    }
    onClick()
  }, [options, onClick])

  const handleChange = React.useCallback(
    (updatedValue: string) => () => {
      if (updatedValue === value) {
        if (canDeselect) {
          onChange('')
        }
        return
      }
      onChange(updatedValue)
    },
    [onChange, value, canDeselect]
  )

  const handleTransitionState = React.useCallback(
    (state: typeof transitionState) => () => {
      setTransitionState(state)
    },
    []
  )

  return (
    <div className="mb-4 flex flex-col overflow-hidden bg-white">
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={handleClick}
        role="none"
      >
        <div className="flex flex-col gap-1">
          <p className="text-[13px] font-semibold leading-[15.5px] text-[#666666]">
            {label}
          </p>
          <SwitchTransition>
            <CSSTransition
              key={`${selectedOption?.id}_${selectedOption?.name}`}
              classNames="transition-lot-select-property-text-change"
              timeout={300}
            >
              <p
                className={`text-[18px] font-medium leading-[21.5px] ${
                  selectedOption?.name ? 'text-black' : 'text-[#666666]'
                }`}
              >
                {selectedOption?.name || placeholder}
              </p>
            </CSSTransition>
          </SwitchTransition>
        </div>
        {options.length ? (
          <ChevronSvg
            className="h-5 w-5 transition-all duration-500"
            rotate={showOptions ? 'top' : 'down'}
            stroke={2}
          />
        ) : null}
      </div>
      <CSSTransition
        in={showOptions}
        classNames="transition-lot-select-property-option-expand"
        timeout={500}
        onEnter={handleTransitionState('enter')}
        onExited={handleTransitionState('exited')}
      >
        <div className="mt-3 grid grid-cols-2 gap-[10px]">
          {(!showOptions && transitionState === 'exited' ? [] : options).map(
            (option) => (
              <PropertyOption
                key={option.id}
                option={option}
                isSelected={value === option.id}
                onClick={handleChange(option.id)}
                showAsThumbnail={!!showAsThumbnail}
              />
            )
          )}
        </div>
      </CSSTransition>
    </div>
  )
}

export default SelectProperty
